/**
 * Select — native HTML <select> styled to match the source's gradient-arrow
 * dropdown. RN has no select primitive, so on web we render a real <select>
 * (createElement) which gives correct a11y + keyboard behavior. The gradient
 * arrow + tokens replicate the source exactly.
 */
import React from 'react';
import { fontFamily, fontSize, fontWeight } from '../theme';

export type Option = { value: string; label: string };

type Props = {
  value: string;
  options: Option[];
  onChange: (v: string) => void;
  variant?: 'group' | 'pill' | 'plain' | 'field';
  height?: number;
  mono?: boolean;
  title?: string;
  style?: React.CSSProperties;
};

const arrow = (rightA: number, rightB: number): React.CSSProperties => ({
  backgroundImage:
    'linear-gradient(45deg, transparent 50%, var(--ink-3) 50%), linear-gradient(135deg, var(--ink-3) 50%, transparent 50%)',
  backgroundPosition: `calc(100% - ${rightA}px) 50%, calc(100% - ${rightB}px) 50%`,
  backgroundSize: '5px 5px, 5px 5px',
  backgroundRepeat: 'no-repeat',
});

export default function Select({ value, options, onChange, variant = 'field', height = 38, mono, title, style }: Props) {
  const base: React.CSSProperties = {
    appearance: 'none',
    WebkitAppearance: 'none',
    fontFamily: mono ? fontFamily.mono : fontFamily.sans,
    color: 'var(--ink)',
    cursor: 'pointer',
    outline: 'none',
    border: '1px solid var(--line)',
    background: 'var(--card)',
  };

  let extra: React.CSSProperties = {};
  if (variant === 'field') {
    extra = { height, padding: '0 30px 0 12px', borderRadius: 5, fontSize: fontSize.md, ...arrow(16, 11) };
  } else if (variant === 'pill') {
    extra = { height: 32, padding: '0 26px 0 10px', borderRadius: 999, fontSize: fontSize.sm, fontWeight: fontWeight.medium as any, color: 'var(--ink-2)', ...arrow(13, 8) };
  } else if (variant === 'group') {
    extra = { height, padding: '0 28px 0 12px', borderRadius: 5, border: '1px solid var(--line-strong)', background: 'var(--paper-2)', fontFamily: fontFamily.mono, fontSize: fontSize.sm, ...arrow(15, 10) };
  } else {
    // plain — used in the builder header (transparent, semibold)
    extra = { padding: '0 20px 0 0', border: 'none', background: 'transparent', fontWeight: fontWeight.semibold as any, fontSize: fontSize.lg, ...arrow(7, 3) };
    (extra as any).backgroundPosition = 'calc(100% - 7px) 60%, calc(100% - 3px) 60%';
    (extra as any).backgroundSize = '4px 4px, 4px 4px';
  }

  return React.createElement(
    'select',
    {
      value,
      title,
      onChange: (e: React.ChangeEvent<HTMLSelectElement>) => onChange(e.target.value),
      style: { ...base, ...extra, ...style },
    },
    options.map(o => React.createElement('option', { key: o.value, value: o.value }, o.label)),
  );
}
